import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { FC, useEffect, useMemo, useState } from 'react'

import { gMapsApiKey } from '@/config/env'
import { center } from '@/modules/investment/distribution/components/map/Maps'
import { getDetailSezRegions } from '@/services/sez-regions'
import { BaseRequest } from '@/types/sez-regions'
import clsxm from '@/utils/clsxm'
import { useAppSelector } from '@/utils/hooks'

import Contact from './tabs/Contact'
import Infrastructure from './tabs/Infrastructure'
import InvestmentOpportunities from './tabs/InvestmentOpportunities'
import Media from './tabs/Media'
import Profile from './tabs/Profile'
import Regulation from './tabs/Regulation'

const TabsDetail: FC<{
  hitContact: { hitContact: boolean; setHitContact: React.Dispatch<React.SetStateAction<boolean>> }
}> = ({ hitContact: { hitContact, setHitContact } }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const [selectedTabs, setSelectedTabs] = useState(0)
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: String(gMapsApiKey),
  })

  const [tabs] = useState([
    {
      id: 0,
      name: t('common:profile'),
      content: <Profile />,
    },
    {
      id: 1,
      name: t('common:regulation'),
      content: <Regulation />,
    },
    {
      id: 2,
      name: t('common:infrastructure'),
      content: <Infrastructure />,
    },
    {
      id: 3,
      name: t('common:InvestmentOpportunity'),
      content: <InvestmentOpportunities />,
    },
    {
      id: 4,
      name: 'Media',
      content: <Media />,
    },
    {
      id: 5,
      name: t('common:contact'),
      content: <Contact />,
    },
  ])

  const [options, setOptions] = useState<google.maps.MapOptions>({
    center,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    keyboardShortcuts: false,
    zoomControl: false,
    draggable: false,
    styles: [
      {
        featureType: 'poi',
        stylers: [{ visibility: 'off' }],
      },
    ],
    zoom: 12,
  })

  const request: BaseRequest = useMemo(() => {
    return {
      params: { slug: String(router.query?.slug) },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data } = useAppSelector(getDetailSezRegions.select(request))

  const locations = useMemo(() => {
    return data?.data.infrastructures.map((infrastructure) => ({
      ...infrastructure,
      position: { lat: infrastructure.latitude, lng: infrastructure.longitude },
    }))
  }, [data?.data.infrastructures])

  useEffect(() => {
    if (router.query.activeTab) {
      const tabActive = tabs.find((item) => item.name.toLowerCase() == String(router.query.activeTab).toLowerCase())?.id
      setSelectedTabs(Number(tabActive))
    }
  }, [router.query.activeTab])

  useEffect(() => {
    setOptions((prevState) => ({
      ...prevState,
      center: { lat: Number(data?.data.latitude), lng: Number(data?.data.longitude) },
    }))
  }, [data?.data])

  useEffect(() => {
    if (hitContact) {
      setSelectedTabs(5)
      setHitContact(false)
    }
  }, [hitContact])

  return (
    <div className='flex w-full flex-col pt-6 lg:pt-8'>
      <div className='flex h-[44px] w-full overflow-auto rounded-t-lg lg:container md:h-[74px]'>
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            className={clsxm(
              'flex h-full w-full items-center justify-center bg-tab-color px-4 py-[13px] text-gray-2 transition-all duration-300',
              tab.id == selectedTabs && 'border-t-4 border-t-yellow bg-white pt-[9px] font-bold text-red',
              index == 0 && 'ml-4 rounded-tl-lg lg:ml-0',
              index == tabs.length - 1 && 'mr-4 rounded-tr-lg lg:mr-0'
            )}
            onClick={() => setSelectedTabs(tab.id)}
          >
            <span
              className={clsxm(
                'whitespace-nowrap text-xs font-semibold text-inherit md:font-medium lg:text-base lg:tracking-[=0.176px]',
                tab.id == selectedTabs && 'font-bold'
              )}
            >
              {tab.name}
            </span>
          </button>
        ))}
      </div>

      <div className={clsxm('flex flex-col gap-6 bg-white py-4 lg:gap-8 lg:py-12.5', selectedTabs === 0 && 'py-6')}>
        <div className='w-full transition-all duration-300'>{tabs.find((tab) => tab.id == selectedTabs)?.content}</div>

        <div className='container flex flex-col gap-4 lg:gap-8'>
          <p className='text-lg font-bold leading-7 text-gray-1 lg:text-2xl lg:leading-8'>{t('common:location')}</p>
          {isLoaded && (
            <GoogleMap
              options={options}
              mapContainerClassName='relative h-[420px] rounded-lg bg-neutral-disable'
            >
              {locations && locations.length > 0 ? (
                <>
                  {locations.map((item) => (
                    <Marker
                      key={item.xid}
                      position={item.position}
                      icon={{
                        url: String(item.category.iconMarker),
                        scaledSize: new window.google.maps.Size(28, 35),
                      }}
                    />
                  ))}
                </>
              ) : null}

              <div className='absolute bottom-0 flex w-full flex-col gap-4 rounded-b-lg bg-[#202020]/80 px-8 py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-[62px]'>
                <div className='flex w-full items-center gap-2 lg:max-w-[522px]'>
                  <span className='kek-location text-xl text-yellow' />
                  <p className='line-clamp-2 tracking-[-0.176px] text-white'>{data?.data.address}</p>
                </div>

                <Link
                  href={{
                    pathname: '/investment/distribution',
                    query: { slug: String(data?.data.slug) },
                  }}
                  className='flex w-full items-center justify-center gap-2 rounded-full bg-red px-5 py-2.5 md:py-3 lg:w-fit'
                >
                  <span className='font-bold text-white'>{t('common:viewOnMaps')}</span>
                  <span className='kek-arrow-up-right text-xl text-white' />
                </Link>
              </div>
            </GoogleMap>
          )}
        </div>
      </div>
    </div>
  )
}

export default TabsDetail
